import React, { useEffect, useRef, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import HtmlCssProject, { HtmlCssProjectRef } from '../../components/HtmlCss/HtmlCssProject';
import AIBuilderProject, { AIBuilderProjectRef } from '../../components/AIBuilder/AIBuilderProject';

import ProjectCmntAndNote from '../../components/cards/ProjectCmntAndNoteCard';
import { useWorkflowApi } from '../../api/workflow/workflow';
import { useParams, useNavigate } from 'react-router-dom';
import { TProject } from '../../models/project/project';
import { TWorkflowProjectDetails, TWorkflowStep } from '../../models/workflow/workflow';
import { Button, message, Modal, Result, Space } from 'antd';
import NotesHistory from './NotesHistory';
import { WORKFLOW_STEPS } from '../../constant/workflow';
import { useProjectApi } from '../../api/projects/projects';
import { useReferential } from '../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../constant/codification';
import IDEPhaserProject, { IDEPhaserProjectRef } from '../../components/IDEPhaser/IDEPhaserProject';
import BlocklyPhaserProject, { BlocklyPhaserProjectRef } from '../../components/BlocklyPhaserProject/BlocklyPhaserProject';
import { PROJECT_TYPE_CODE, resolveProjectType } from '../../constant/projectType';

const CreationDetails: React.FC = () => {
  const { getProjectById } = useProjectApi();
  const {
    getNextButton,
    createNextStep,
    createBackStep,
  } = useWorkflowApi();
  const { getCodificationById, isReferentialLoaded } = useReferential();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectIdNum = Number(projectId);

  const [project, setProject] = useState<TProject | null>(null);
  const [step, setStep] = useState<TWorkflowStep | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<boolean>(false);
  const [isModalVisibleNote, setIsModalVisibleNote] = useState(false);
  const [isModalVisibleNoteHistory, setIsModalVisibleNoteHistory] = useState(false);
  const [isBackAction, setIsBackAction] = useState(false);
  const projectRef = useRef<IDEPhaserProjectRef | HtmlCssProjectRef | AIBuilderProjectRef | BlocklyPhaserProjectRef>(null);
  const [canSave, setCanSave] = useState<any>();
  const [aiBuilderReady, setAiBuilderReady] = useState(false);

  // Charger le projet
  useEffect(() => {
    setLoading(true);
    getProjectById(projectIdNum)
      .then(projectRes => {
        setProject(projectRes.data);
        setAiBuilderReady(false);
        return getNextButton(projectIdNum);
      })
      .then(stepRes => {
        setStep(stepRes.data);
      })
      .catch((err) => {
        message.error(err.response?.data ?? err.message ?? err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line 
  }, [projectIdNum]);

  useEffect(() => {
    const cansave = step?.code === WORKFLOW_STEPS.SUBMIT && projectRef.current;
    setCanSave(cansave)
  }, [step, projectRef])

  // Résolution par id puis par code exact. Tant que le référentiel n'est pas
  // chargé on ne résout rien : un id encore introuvable n'est pas un type inconnu.
  const projectTypeCodif = project ? getCodificationById(project.project_type_id) : undefined;
  const projectType = project && isReferentialLoaded
    ? resolveProjectType(
        projectTypeCodif?.code_type === CODIFICATION_TYPE.PROJECT_TYPE ? projectTypeCodif.code : undefined
      )
    : null;
  const projectTypeUnknown = projectType?.kind === 'unknown';

  // Ouverture du popup
  const handleNextClick = () => {
    if (step && step.code === WORKFLOW_STEPS.REVIEW) {
      setIsBackAction(false)
      setIsModalVisibleNote(true);
    } else {
      handleNext()
    }
  };

  // Ouvre le modal pour revenir à l'étape précédente
  const handleBackClick = () => {
    if (step && step.code === WORKFLOW_STEPS.REVIEW) {
      setIsBackAction(true)
      setIsModalVisibleNote(true);
    } else {
      handleBack()
    }
  };

  const handleShowHistory = () => setIsModalVisibleNoteHistory(true);

  const handleNext = (details?: TWorkflowProjectDetails) => {
    handlePublish(true, details)
  }
  const handleBack = (details?: TWorkflowProjectDetails) => {
    handlePublish(false, details)
  }
  // Soumission Back
  const handlePublish = async (next: boolean = true, details?: TWorkflowProjectDetails) => {
    setLoadingAction(true);
    try {
      if (step?.code === WORKFLOW_STEPS.SUBMIT) {
        // Attendu : sans cela l'etape avance et le message de succes s'affiche
        // pendant que l'enregistrement est encore en vol. En cas d'echec, le
        // travail est perdu et l'etape n'est plus modifiable.
        await projectRef.current?.save()
      }
      if (next) {
        await createNextStep(projectIdNum, details);
      } else {
        await createBackStep(projectIdNum, details);
      }

      message.success('Enregistré avec succès');
      (getNextButton(projectIdNum))
        .then(res => {
          setStep(res.data);
        });
    } catch {
      message.error('Échec lors de l\'enregistrement.');
    } finally {
      setLoadingAction(false);
      setIsModalVisibleNote(false)
    }
  };

  // Un éditeur par code exact ; pas de branche par défaut (le compilateur
  // vérifie que chaque code de PROJECT_TYPE_CODE a son éditeur).
  const renderProjectComponent = () => {
    if (!project || !projectType) return null;

    if (projectType.kind === 'unknown') {
      return (
        <Result
          status="warning"
          title="Type de projet inconnu"
          subTitle={
            `Le projet porte le type « ${projectType.received || '(aucun)'} » ` +
            `(project_type_id=${project.project_type_id}), absent de la table des types. ` +
            `L'éditeur n'est pas ouvert pour ne pas écraser le travail : le type doit être corrigé dans les données.`
          }
        />
      );
    }

    const enableEditCode =
      step !== null && step !== undefined && step.code === WORKFLOW_STEPS.SUBMIT;

    switch (projectType.code) {
      case PROJECT_TYPE_CODE.BLOCKLY_GAMING:
        return (
          <BlocklyPhaserProject
            ref={projectRef as React.RefObject<BlocklyPhaserProjectRef>}
            project={project}
            enableEditCode={enableEditCode}
          />
        );
      case PROJECT_TYPE_CODE.HTMLCSS_WEB:
        return (
          <HtmlCssProject
            ref={projectRef as React.RefObject<HtmlCssProjectRef>}
            project={project}
            enableEditCode={enableEditCode}
          />
        );
      case PROJECT_TYPE_CODE.AI_BUILDER:
        return (
          <AIBuilderProject
            ref={projectRef as React.RefObject<AIBuilderProjectRef>}
            project={project}
            enableEditCode={enableEditCode}
            onReadinessChange={setAiBuilderReady}
          />
        );
      case PROJECT_TYPE_CODE.PYTHON_GAMING:
        return (
          <IDEPhaserProject
            ref={projectRef as React.RefObject<IDEPhaserProjectRef>}
            project={project}
            enableEditCode={enableEditCode}
            language="python"
          />
        );
      case PROJECT_TYPE_CODE.JAVASCRIPT_GAMING:
        return (
          <IDEPhaserProject
            ref={projectRef as React.RefObject<IDEPhaserProjectRef>}
            project={project}
            enableEditCode={enableEditCode}
            language="javascript"
          />
        );
      default: {
        const exhaustive: never = projectType.code;
        return exhaustive;
      }
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout 
      title={project?.name || ''} 
      loading={loading}
      onBackNavigation={handleBackNavigation}
      showBackButton={true}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <Space>
          {step?.back_button && !projectTypeUnknown && (
            <Button
              loading={loadingAction}
              onClick={handleBackClick}
            >
              {step.back_button}
            </Button>
          )}

          {/* Type inconnu : aucun éditeur, donc rien à enregistrer ; avancer
              l'étape soumettrait un projet vide. */}
          {step?.next_button && !projectTypeUnknown &&
            !(projectType?.kind === 'known' && projectType.code === PROJECT_TYPE_CODE.AI_BUILDER
              && step.code === WORKFLOW_STEPS.SUBMIT && !aiBuilderReady) && (
            <Button
              type="primary"
              loading={loadingAction}
              onClick={handleNextClick}
            >
              {step.next_button}
            </Button>
          )}

          <Button
            type="link"
            onClick={handleShowHistory}
          >
            Historique
          </Button>
        </Space>
      </div>

      {project && renderProjectComponent()}

      <Modal
        title="Ajouter des détails et commentaire"
        open={isModalVisibleNote}
        footer={null}
        onCancel={() => setIsModalVisibleNote(false)}
        destroyOnClose
      >
        <ProjectCmntAndNote
          isEditable
          workflowProject={undefined}
          onSubmit={isBackAction ? handleBack : handleNext}
        />
      </Modal>
      <NotesHistory
        projectId={projectIdNum}
        open={isModalVisibleNoteHistory}
        onClose={() => setIsModalVisibleNoteHistory(false)}
      />
    </AppLayout>
  );
};

export default CreationDetails;
